/**
 * academyBuilder — sub-container do slice acadêmico.
 *
 * Agrupa: Cohort, Catalog (read), Course, Discipline, Curriculum,
 * Matricula (com helper compartilhado pra transitionStatus), Contrato, Parcela.
 *
 * Mantém Matricula get use case exposto pra reaproveitar no
 * TransitionMatriculaStatus (depende de pré-leitura).
 *
 * @since F.5-polish-extras-3
 */
import type { $Fetch } from 'ofetch';

import { HttpCohortGateway } from '@/infrastructure/http/HttpCohortGateway';
import { HttpCatalogReadGateway } from '@/infrastructure/http/HttpCatalogReadGateway';
import { HttpCourseGateway } from '@/infrastructure/http/HttpCourseGateway';
import { HttpDisciplineGateway } from '@/infrastructure/http/HttpDisciplineGateway';
import { HttpCurriculumGateway } from '@/infrastructure/http/HttpCurriculumGateway';
import { HttpMatriculaGateway } from '@/infrastructure/http/HttpMatriculaGateway';
import { HttpContratoGateway } from '@/infrastructure/http/HttpContratoGateway';
import { HttpParcelaGateway } from '@/infrastructure/http/HttpParcelaGateway';

import {
  CreateCohortUseCaseImpl, GetCohortUseCaseImpl,
  ListCohortsUseCaseImpl, UpdateCohortUseCaseImpl,
} from '@/application/usecase/CohortUseCaseImpl';
import {
  ListCoursesUseCaseImpl, ListCurriculumsByCourseUseCaseImpl,
} from '@/application/usecase/CatalogQueryUseCaseImpl';
import {
  ArchiveCourseUseCaseImpl, CreateCourseUseCaseImpl, GetCourseUseCaseImpl,
  ListCoursesPaginatedUseCaseImpl, PublishCourseUseCaseImpl, UpdateCourseUseCaseImpl,
  ListCourseDisciplinesUseCaseImpl, ChooseCourseCurriculumUseCaseImpl,
} from '@/application/usecase/CourseUseCaseImpl';
import {
  CreateDisciplineUseCaseImpl, GetDisciplineUseCaseImpl,
  ListDisciplinesUseCaseImpl, UpdateDisciplineUseCaseImpl,
  ActivateDisciplineUseCaseImpl, ArchiveDisciplineUseCaseImpl,
} from '@/application/usecase/DisciplineUseCaseImpl';
import {
  ListCurriculumsUseCaseImpl, ArchiveCurriculumUseCaseImpl, CreateCurriculumUseCaseImpl, GetCurriculumUseCaseImpl,
  ListCurriculumsByCourseFullUseCaseImpl, PublishCurriculumUseCaseImpl, UpdateCurriculumUseCaseImpl,
} from '@/application/usecase/CurriculumUseCaseImpl';
import {
  CreateMatriculaUseCaseImpl, GetMatriculaUseCaseImpl,
  ListMatriculasByAlunoUseCaseImpl, ListMatriculasUseCaseImpl,
  TransitionMatriculaStatusUseCaseImpl,
} from '@/application/usecase/MatriculaUseCaseImpl';
import {
  CancelarContratoUseCaseImpl, GerarContratoUseCaseImpl, GetContratoUseCaseImpl,
  ListContratosByMatriculaUseCaseImpl, RenderizarContratoUseCaseImpl,
  RegistrarAssinaturaContratoUseCaseImpl,
} from '@/application/usecase/ContratoUseCaseImpl';
import {
  CancelarParcelaUseCaseImpl, CreateParcelaUseCaseImpl,
  ListParcelasUseCaseImpl, PagarParcelaUseCaseImpl,
} from '@/application/usecase/ParcelaUseCaseImpl';

import type { CohortGateway } from '@/application/port/out/CohortGateway';
import type { CatalogReadGateway } from '@/application/port/out/CatalogReadGateway';
import type { CourseGateway } from '@/application/port/out/CourseGateway';
import type { DisciplineGateway } from '@/application/port/out/DisciplineGateway';
import type { CurriculumGateway } from '@/application/port/out/CurriculumGateway';
import type { MatriculaGateway } from '@/application/port/out/MatriculaGateway';
import type { ContratoGateway } from '@/application/port/out/ContratoGateway';
import type { ParcelaGateway } from '@/application/port/out/ParcelaGateway';

export function buildAcademySlice(httpClient: $Fetch) {
  const cohortGateway: CohortGateway = new HttpCohortGateway(httpClient);
  const catalogReadGateway: CatalogReadGateway = new HttpCatalogReadGateway(httpClient);
  const courseGateway: CourseGateway = new HttpCourseGateway(httpClient);
  const disciplineGateway: DisciplineGateway = new HttpDisciplineGateway(httpClient);
  const curriculumGateway: CurriculumGateway = new HttpCurriculumGateway(httpClient);
  const matriculaGateway: MatriculaGateway = new HttpMatriculaGateway(httpClient);
  const contratoGateway: ContratoGateway = new HttpContratoGateway(httpClient);
  const parcelaGateway: ParcelaGateway = new HttpParcelaGateway(httpClient);

  // Mantém helper compartilhado pra TransitionMatriculaStatus (depende de pre-leitura).
  const getMatriculaUseCase = new GetMatriculaUseCaseImpl(matriculaGateway);

  const useCases = {
    listCohorts: new ListCohortsUseCaseImpl(cohortGateway),
    getCohort: new GetCohortUseCaseImpl(cohortGateway),
    createCohort: new CreateCohortUseCaseImpl(cohortGateway),
    updateCohort: new UpdateCohortUseCaseImpl(cohortGateway),
    listCourses: new ListCoursesUseCaseImpl(catalogReadGateway),
    listCurriculumsByCourse: new ListCurriculumsByCourseUseCaseImpl(catalogReadGateway),
    listCoursesPaginated: new ListCoursesPaginatedUseCaseImpl(courseGateway),
    getCourse: new GetCourseUseCaseImpl(courseGateway),
    createCourse: new CreateCourseUseCaseImpl(courseGateway),
    updateCourse: new UpdateCourseUseCaseImpl(courseGateway),
    publishCourse: new PublishCourseUseCaseImpl(courseGateway),
    archiveCourse: new ArchiveCourseUseCaseImpl(courseGateway),
    chooseCourseCurriculum: new ChooseCourseCurriculumUseCaseImpl(courseGateway),
    listCourseDisciplines: new ListCourseDisciplinesUseCaseImpl(courseGateway),
    listDisciplines: new ListDisciplinesUseCaseImpl(disciplineGateway),
    getDiscipline: new GetDisciplineUseCaseImpl(disciplineGateway),
    createDiscipline: new CreateDisciplineUseCaseImpl(disciplineGateway),
    updateDiscipline: new UpdateDisciplineUseCaseImpl(disciplineGateway),
    activateDiscipline: new ActivateDisciplineUseCaseImpl(disciplineGateway),
    archiveDiscipline: new ArchiveDisciplineUseCaseImpl(disciplineGateway),
    listCurriculums: new ListCurriculumsUseCaseImpl(curriculumGateway),
    listCurriculumsFull: new ListCurriculumsByCourseFullUseCaseImpl(curriculumGateway),
    getCurriculum: new GetCurriculumUseCaseImpl(curriculumGateway),
    createCurriculum: new CreateCurriculumUseCaseImpl(curriculumGateway),
    updateCurriculum: new UpdateCurriculumUseCaseImpl(curriculumGateway),
    publishCurriculum: new PublishCurriculumUseCaseImpl(curriculumGateway),
    archiveCurriculum: new ArchiveCurriculumUseCaseImpl(curriculumGateway),
    listMatriculas: new ListMatriculasUseCaseImpl(matriculaGateway),
    getMatricula: getMatriculaUseCase,
    listMatriculasByAluno: new ListMatriculasByAlunoUseCaseImpl(matriculaGateway),
    createMatricula: new CreateMatriculaUseCaseImpl(matriculaGateway),
    transitionMatriculaStatus: new TransitionMatriculaStatusUseCaseImpl(
      matriculaGateway,
      (id) => getMatriculaUseCase.execute(id),
    ),
    listContratos: new ListContratosByMatriculaUseCaseImpl(contratoGateway),
    getContrato: new GetContratoUseCaseImpl(contratoGateway),
    gerarContrato: new GerarContratoUseCaseImpl(contratoGateway),
    renderizarContrato: new RenderizarContratoUseCaseImpl(contratoGateway),
    cancelarContrato: new CancelarContratoUseCaseImpl(contratoGateway),
    registrarAssinaturaContrato: new RegistrarAssinaturaContratoUseCaseImpl(contratoGateway),
    listParcelas: new ListParcelasUseCaseImpl(parcelaGateway),
    createParcela: new CreateParcelaUseCaseImpl(parcelaGateway),
    pagarParcela: new PagarParcelaUseCaseImpl(parcelaGateway),
    cancelarParcela: new CancelarParcelaUseCaseImpl(parcelaGateway),
  };

  // Gateways expostos pelo Container interface pra views/composables que ainda
  // os acessam diretamente (catalogReadGateway, etc).
  const gateways = {
    cohortGateway, catalogReadGateway, courseGateway, disciplineGateway,
    curriculumGateway, matriculaGateway, contratoGateway, parcelaGateway,
  };

  return { useCases, gateways };
}
